import React from 'react';
import { SectionCard } from '../../components/sections/SectionCard';
import { PanelTable } from '../../components/display/PanelTable';
import { AppStatusBadge } from '@shared/components/ui/status-badge/AppStatusBadge';
import { formatCurrency, formatDate } from '@shared/utils/formatters';
import type { PanelTableColumn } from '../../components/display/PanelTable/types';
import { mockDisputes } from '@shared/data/mockDisputes';
import type { Dispute } from '@features/disputes/types/disputes.types';

interface DisputesSectionProps {
  isDetailView?: boolean;
  disputes?: Dispute[];
  onViewDispute?: (dispute: Dispute) => void;
  onViewPayment?: (dispute: Dispute) => void;
  onSubmitEvidence?: (dispute: Dispute) => void;
  onConcede?: (dispute: Dispute) => void;
}

const renderAmount = (_: unknown, dispute: Dispute) => (
  <div className="font-mono text-sm">
    {formatCurrency(dispute.amount, dispute.currency)}
  </div>
);

const renderStatus = (_: unknown, dispute: Dispute) => (
  <AppStatusBadge entityType="dispute" status={dispute.status} size="sm" />
);

const renderReason = (_: unknown, dispute: Dispute) => {
  const reasonLabels: Record<string, string> = {
    'duplicate': 'Duplicate',
    'fraudulent': 'Fraudulent',
    'subscription_canceled': 'Subscription Canceled',
    'product_unacceptable': 'Product Unacceptable',
    'product_not_received': 'Product Not Received',
    'unrecognized': 'Unrecognized',
    'credit_not_processed': 'Credit Not Processed',
    'general': 'General',
    'other': 'Other'
  };

  return (
    <span className="text-xs text-muted-foreground">
      {reasonLabels[dispute.reason] || dispute.reason}
    </span>
  );
};

const renderDate = (_: unknown, dispute: Dispute) => (
  <span className="text-xs">{formatDate(dispute.created)}</span>
);

const renderPayment = (_: unknown, dispute: Dispute) => (
  <span className="text-xs font-mono text-muted-foreground">
    {dispute.paymentId}
  </span>
);

const renderDueDate = (_: unknown, dispute: Dispute) => {
  if (!dispute.evidenceDueBy) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }

  const dueDate = new Date(dispute.evidenceDueBy);
  const now = new Date();
  const isOverdue = dueDate < now;
  const needsResponse = ['needs_response', 'warning_needs_response', 'under_review', 'warning_under_review'].includes(dispute.status);

  return (
    <span className={`text-xs ${isOverdue && needsResponse ? 'text-destructive font-medium' : ''}`}>
      {formatDate(dispute.evidenceDueBy)}
    </span>
  );
};

export const DisputesSection = React.memo<DisputesSectionProps>(({
  isDetailView = false,
  disputes,
  onViewDispute,
  onViewPayment,
  onSubmitEvidence,
  onConcede,
}) => {
  const columns: PanelTableColumn<Dispute>[] = [
    {
      key: 'amount',
      label: 'AMOUNT',
      render: renderAmount,
      headerClassName: 'w-[120px]'
    },
    {
      key: 'status',
      label: 'STATUS',
      render: renderStatus,
      headerClassName: 'w-[130px]'
    },
    {
      key: 'reason',
      label: 'REASON',
      render: renderReason,
      headerClassName: 'w-[150px]'
    },
    {
      key: 'paymentId',
      label: 'PAYMENT',
      render: renderPayment,
      headerClassName: 'w-[140px]'
    },
    {
      key: 'evidenceDueBy',
      label: 'DUE BY',
      render: renderDueDate,
      headerClassName: 'w-[120px]'
    },
    {
      key: 'created',
      label: 'CREATED',
      render: renderDate,
      headerClassName: 'w-[120px]'
    },
  ];

  const rowActions = (dispute: Dispute) => {
    const actions = [
      {
        key: 'view-dispute',
        label: 'View dispute',
        onClick: () => onViewDispute?.(dispute)
      },
      {
        key: 'view-payment',
        label: 'View payment',
        onClick: () => onViewPayment?.(dispute)
      },
    ];

    const isActionable = ['needs_response', 'warning_needs_response'].includes(dispute.status);
    const canConcede = ['needs_response', 'warning_needs_response', 'under_review', 'warning_under_review'].includes(dispute.status);

    if (isActionable) {
      actions.push({
        key: 'submit-evidence',
        label: 'Submit evidence',
        onClick: () => onSubmitEvidence?.(dispute)
      });
    }

    if (canConcede) {
      actions.push({
        key: 'accept-dispute',
        label: 'Concede dispute',
        onClick: () => onConcede?.(dispute)
      });
    }

    return actions;
  };

  return (
    <SectionCard title="Disputes" layout="down">
      <PanelTable
        data={disputes ?? mockDisputes}
        columns={columns}
        variant={isDetailView ? 'full' : 'panel'}
        emptyMessage="No disputes found for this customer"
        pageSize={5}
        rowActions={rowActions}
      />
    </SectionCard>
  );
});

DisputesSection.displayName = 'DisputesSection';
